import { apiFetch } from "./client";

/**
 * How a class criterion compares a finding against its value. ABSENT and PRESENT take no
 * value: they say the thing was or was not found.
 */
export type QualityClassCriterionOperator =
  | "LT"
  | "LTE"
  | "GT"
  | "GTE"
  | "EQ"
  | "ABSENT"
  | "PRESENT";

/**
 * One criterion that distinguishes a class from the next.
 *
 * A measurable one carries the test, the operator and the value. A descriptive one carries
 * words the laboratory confirms by eye — `descriptive` says which, so a confirmation is
 * never drawn as a comparison.
 */
export type QualityClassCriterion = {
  id: string;
  parameterId: string | null;
  parameterKey: string | null;
  parameterNameI18n: Record<string, string> | null;
  unit: string | null;
  operator: QualityClassCriterionOperator | null;
  valueNum: number | null;
  valueBool: boolean | null;
  description: string | null;
  descriptive: boolean;
  sortOrder: number;
};

/**
 * A class the raw material is put into on intake.
 *
 * `unloadingPlace` and `usedFor` are the reason the class is decided at all — it says which
 * silo the lorry goes to and what the material may be made into. A screen that leaves them
 * out has shown a label and hidden the meaning.
 *
 * `affectsPrice` is shown and read by nothing. Whether the class bears on what a
 * co-operative is paid is still open between the quality service and procurement; the field
 * is here so the question can be settled, not so the portal can answer it on its own.
 */
export type QualityClass = {
  id: string;
  culture: string;
  code: string;
  nameI18n: Record<string, string> | null;
  unloadingPlace: string | null;
  usedFor: string | null;
  affectsPrice: boolean;
  sortOrder: number;
  active: boolean;
  criteria: QualityClassCriterion[];
};

export type SaveQualityClassBody = {
  culture: string;
  code: string;
  nameI18n?: Record<string, string>;
  unloadingPlace?: string | null;
  usedFor?: string | null;
  affectsPrice: boolean;
  sortOrder: number;
  active?: boolean;
};

export type SaveCriterionBody = {
  parameterId?: string | null;
  operator?: QualityClassCriterionOperator | null;
  valueNum?: number | null;
  valueBool?: boolean | null;
  description?: string | null;
  sortOrder: number;
};

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export const qualityClassesApi = {
  list: (params: { culture?: string; includeInactive?: string } = {}) =>
    apiFetch<QualityClass[]>(`/quality-classes${q(params)}`),
  cultures: () => apiFetch<string[]>("/quality-classes/cultures"),
  get: (id: string) => apiFetch<QualityClass>(`/quality-classes/${id}`),
  create: (body: SaveQualityClassBody) =>
    apiFetch<QualityClass>("/quality-classes", { method: "POST", body }),
  update: (id: string, body: SaveQualityClassBody) =>
    apiFetch<QualityClass>(`/quality-classes/${id}`, { method: "PUT", body }),
  retire: (id: string) => apiFetch<void>(`/quality-classes/${id}`, { method: "DELETE" }),
  addCriterion: (id: string, body: SaveCriterionBody) =>
    apiFetch<QualityClass>(`/quality-classes/${id}/criteria`, { method: "POST", body }),
  updateCriterion: (id: string, criterionId: string, body: SaveCriterionBody) =>
    apiFetch<QualityClass>(`/quality-classes/${id}/criteria/${criterionId}`, { method: "PUT", body }),
  removeCriterion: (id: string, criterionId: string) =>
    apiFetch<QualityClass>(`/quality-classes/${id}/criteria/${criterionId}`, { method: "DELETE" }),
};

/** A criterion in one line, the way the codebook screen reads it. */
export function criterionText(c: QualityClassCriterion, parameterName: string): string {
  if (c.descriptive) return c.description ?? "";
  const op =
    c.operator === "LT" ? "<"
    : c.operator === "LTE" ? "≤"
    : c.operator === "GT" ? ">"
    : c.operator === "GTE" ? "≥"
    : c.operator === "EQ" ? "="
    : null;
  if (op == null) {
    // ABSENT and PRESENT compare against nothing.
    return `${parameterName}: ${c.operator === "ABSENT" ? "nije prisutno" : "prisutno"}`;
  }
  const value = c.valueNum != null ? String(c.valueNum) : c.valueBool != null ? (c.valueBool ? "da" : "ne") : "";
  return `${parameterName} ${op} ${value}${c.unit ? ` ${c.unit}` : ""}`;
}
